import { Input } from "@/components/ui/input";
import { GripVertical, RefreshCw, Trash2, ChevronDown, ChevronUp, ChevronRight, Camera, Pencil, Check, Flame, Dumbbell, Heart } from "lucide-react";
import { useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const SECTIONS = [
  { id: "warmup", label: "Aquecimento (Mobilidade)", labelDE: "Aufwärmen (Mobilität)", icon: Flame, color: "amber", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
  { id: "main", label: "Treino Principal", labelDE: "Haupttraining", icon: Dumbbell, color: "blue", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-100 text-blue-700" },
  { id: "cooldown", label: "Alongamento", labelDE: "Dehnung / Cool-Down", icon: Heart, color: "emerald", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700" },
];

export default function ExerciseList({
  exercises, activeDay, getMuscleLabel,
  onRegenerate, regeneratingIdx, onRemove, onUpdate,
  expandedExercise, setExpandedExercise,
  dragIdx, dragOverIdx, onDragStart, onDragOver, onDrop, onDragEnd,
  onAutoSavePlan, days,
}) {
  const fileInputRefs = useRef({});
  const [editingName, setEditingName] = useState(null);
  const [editingDesc, setEditingDesc] = useState(null);
  const [nameValue, setNameValue] = useState("");
  const [descValue, setDescValue] = useState("");
  const savingRef = useRef({});
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleImageUpload = async (idx, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/exercises/upload-image`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      });
      const newUrl = `${API}/files/${res.data.path}`;
      onUpdate(idx, "image", newUrl);
      saveToExerciseDB(exercises[idx].exercise_id, { image: newUrl });
      if (onAutoSavePlan && days) {
        const updatedDays = days.map((d, i) =>
          i === activeDay ? { ...d, exercises: d.exercises.map((ex, j) => j === idx ? { ...ex, image: newUrl } : ex) } : d
        );
        onAutoSavePlan(updatedDays);
      }
      toast.success("Bild aktualisiert & Plan gespeichert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    }
  };

  const saveToExerciseDB = useCallback(async (exerciseId, fields) => {
    if (!exerciseId || savingRef.current[exerciseId]) return;
    savingRef.current[exerciseId] = true;
    try {
      await axios.put(`${API}/exercises/database/${exerciseId}`, fields, { headers: getHeaders() });
    } catch {
    } finally {
      savingRef.current[exerciseId] = false;
    }
  }, []);

  const startEditName = (idx) => { setEditingName(idx); setNameValue(exercises[idx].name); };
  const commitName = (idx) => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== exercises[idx].name) {
      onUpdate(idx, "name", trimmed);
      saveToExerciseDB(exercises[idx].exercise_id, { name: trimmed });
      toast.success("Name gespeichert");
    }
    setEditingName(null);
  };

  const startEditDesc = (idx) => {
    setEditingDesc(`${activeDay}-${idx}`);
    setDescValue(exercises[idx].description || "");
    if (expandedExercise !== `${activeDay}-${idx}`) setExpandedExercise(`${activeDay}-${idx}`);
  };
  const commitDesc = (idx) => {
    const trimmed = descValue.trim();
    if (trimmed !== (exercises[idx].description || "")) {
      onUpdate(idx, "description", trimmed);
      saveToExerciseDB(exercises[idx].exercise_id, { description: trimmed });
      toast.success("Beschreibung gespeichert");
    }
    setEditingDesc(null);
  };

  // Group exercises by section (preserving original indices)
  const grouped = SECTIONS.map(sec => ({
    ...sec,
    items: exercises.map((ex, idx) => ({ ex, idx })).filter(({ ex }) => (ex.section || "main") === sec.id),
  }));

  const cycleSectionForExercise = (idx) => {
    const current = exercises[idx].section || "main";
    const order = ["warmup", "main", "cooldown"];
    const next = order[(order.indexOf(current) + 1) % order.length];
    onUpdate(idx, "section", next);
  };

  const renderExerciseRow = ({ ex, idx }, sectionColor) => (
    <div
      key={`${ex.exercise_id}-${idx}`}
      draggable
      onDragStart={() => onDragStart(idx)}
      onDragOver={(e) => onDragOver(e, idx)}
      onDrop={() => onDrop(idx)}
      onDragEnd={onDragEnd}
      className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
        dragOverIdx === idx ? 'border-blue-400 bg-blue-50/50'
        : dragIdx === idx ? 'opacity-40 border-slate-200'
        : 'border-slate-200 bg-white hover:shadow-sm'
      }`}
      data-testid={`exercise-row-${idx}`}
    >
      {/* Drag Handle + Section cycle */}
      <div className="flex flex-col items-center gap-1 pt-2 cursor-grab active:cursor-grabbing select-none" data-testid={`drag-handle-${idx}`}>
        <GripVertical className="w-4 h-4 text-slate-300" />
        <span className="text-[10px] font-bold text-slate-400">{idx + 1}</span>
        <button
          onClick={(e) => { e.stopPropagation(); cycleSectionForExercise(idx); }}
          className={`w-4 h-4 rounded-full ${SECTIONS.find(s => s.id === (ex.section || "main"))?.badge || "bg-slate-100"} flex items-center justify-center`}
          title="Sektion wechseln"
          data-testid={`cycle-section-${idx}`}
        >
          <ChevronRight className="w-2.5 h-2.5" />
        </button>
      </div>

      {/* Image */}
      <div
        className="w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0 rounded-xl overflow-hidden bg-slate-100 relative group/img cursor-pointer shadow-sm"
        onClick={() => fileInputRefs.current[idx]?.click()}
        title="Klicken um Bild zu ändern"
        data-testid={`exercise-image-${idx}`}
      >
        <img src={ex.image} alt={ex.name} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
          <Camera className="w-5 h-5 text-white" />
        </div>
        <input
          ref={el => fileInputRefs.current[idx] = el}
          type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
          onChange={e => { handleImageUpload(idx, e.target.files[0]); e.target.value = ""; }}
          data-testid={`image-upload-${idx}`}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          {editingName === idx ? (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <Input value={nameValue} onChange={e => setNameValue(e.target.value)}
                onBlur={() => commitName(idx)} onKeyDown={e => e.key === "Enter" && commitName(idx)}
                className="h-7 text-sm font-semibold" autoFocus data-testid={`edit-name-input-${idx}`} />
              <button onClick={() => commitName(idx)} className="p-1 rounded hover:bg-emerald-50 text-emerald-600" data-testid={`save-name-${idx}`}>
                <Check className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <h4 className="text-sm font-semibold text-slate-900 truncate cursor-pointer hover:text-blue-700 group/name flex items-center gap-1"
              onClick={() => startEditName(idx)} title="Klicken um Namen zu ändern" data-testid={`exercise-name-${idx}`}>
              {ex.name}
              <Pencil className="w-3 h-3 text-slate-300 opacity-0 group-hover/name:opacity-100 transition-opacity" />
            </h4>
          )}
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            <button onClick={() => onRegenerate(idx)} title="Ersetzen" className="p-1 rounded hover:bg-blue-50 text-blue-500" data-testid={`regenerate-${idx}`}>
              {regeneratingIdx === idx ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => onRemove(idx)} title="Entfernen" className="p-1 rounded hover:bg-red-50 text-red-400" data-testid={`remove-exercise-${idx}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-500 font-medium">
          {getMuscleLabel(ex.muscle_group)}
        </span>

        <div className="flex items-center gap-2 mt-1.5">
          <button
            onClick={() => {
              if (expandedExercise === `${activeDay}-${idx}`) { setExpandedExercise(null); setEditingDesc(null); }
              else setExpandedExercise(`${activeDay}-${idx}`);
            }}
            className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700"
          >
            {expandedExercise === `${activeDay}-${idx}` ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Beschreibung
          </button>
          {expandedExercise === `${activeDay}-${idx}` && editingDesc !== `${activeDay}-${idx}` && (
            <button onClick={() => startEditDesc(idx)} className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-0.5" data-testid={`edit-desc-btn-${idx}`}>
              <Pencil className="w-2.5 h-2.5" /> Bearbeiten
            </button>
          )}
        </div>
        {expandedExercise === `${activeDay}-${idx}` && (
          editingDesc === `${activeDay}-${idx}` ? (
            <div className="mt-1">
              <textarea value={descValue} onChange={e => setDescValue(e.target.value)} onBlur={() => commitDesc(idx)}
                className="w-full text-xs text-slate-600 leading-relaxed bg-emerald-50 p-2 rounded border border-emerald-200 focus:border-emerald-400 focus:outline-none resize-y min-h-[60px]"
                autoFocus rows={3} data-testid={`edit-desc-input-${idx}`} />
              <button onClick={() => commitDesc(idx)} className="mt-1 text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-testid={`save-desc-${idx}`}>
                <Check className="w-3 h-3" /> Speichern
              </button>
            </div>
          ) : (
            <p className="text-xs text-slate-600 mt-1 leading-relaxed bg-emerald-50 p-2 rounded">{ex.description || "Keine Beschreibung"}</p>
          )
        )}

        <div className="grid grid-cols-3 gap-2 mt-2 max-w-sm">
          <div>
            <label className="text-[10px] text-pink-600 font-semibold uppercase">Sätze</label>
            <Input value={ex.sets} onChange={e => onUpdate(idx, "sets", e.target.value)}
              className="h-7 text-xs border-pink-200 focus:border-pink-400" data-testid={`sets-${idx}`} />
          </div>
          <div>
            <label className="text-[10px] text-violet-600 font-semibold uppercase">Wdh.</label>
            <Input value={ex.reps || "12"} onChange={e => onUpdate(idx, "reps", e.target.value)}
              className="h-7 text-xs border-violet-200 focus:border-violet-400" data-testid={`reps-${idx}`} />
          </div>
          <div>
            <label className="text-[10px] text-amber-600 font-semibold uppercase">Gewicht (kg)</label>
            <Input value={ex.weight_kg} onChange={e => onUpdate(idx, "weight_kg", e.target.value)}
              className="h-7 text-xs border-amber-200 focus:border-amber-400" data-testid={`weight-${idx}`} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="exercises-list">
      {grouped.map(section => {
        const SectionIcon = section.icon;
        const isCollapsed = collapsedSections[section.id];
        return (
          <div key={section.id} className={`rounded-xl border ${section.border} overflow-hidden`} data-testid={`section-${section.id}`}>
            {/* Section header - always visible */}
            <button
              onClick={() => toggleSection(section.id)}
              className={`w-full flex items-center justify-between px-4 py-2.5 ${section.bg} transition-colors hover:brightness-95`}
              data-testid={`section-toggle-${section.id}`}
            >
              <div className="flex items-center gap-2">
                <SectionIcon className={`w-4 h-4 ${section.text}`} />
                <span className={`text-sm font-semibold ${section.text}`}>{section.labelDE}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${section.badge} font-medium`}>
                  {section.items.length}
                </span>
              </div>
              <div className={`transition-transform duration-200 ${isCollapsed ? '-rotate-90' : 'rotate-0'}`}>
                <ChevronDown className={`w-4 h-4 ${section.text}`} />
              </div>
            </button>

            {/* Section content - collapsible */}
            {!isCollapsed && (
              <div className="p-2 space-y-2 bg-white" data-testid={`section-content-${section.id}`}>
                {section.items.length > 0 ? (
                  section.items.map(item => renderExerciseRow(item, section.color))
                ) : (
                  <p className={`text-center py-4 text-xs ${section.text} opacity-60`}>
                    Keine Übungen in diesem Abschnitt
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
